import type { BadgeVariant, MenuItem } from '@/data/menu';
import menu from '@/data/menu';
import { defineStore } from 'pinia';

interface MenuState {
    menu: MenuItem[];
    userRoles: string[];
    isSuperAdmin: boolean;
    sectionCollapseState: Record<string, boolean>;
}

function loadCollapseState(): Record<string, boolean> {
    try {
        const parsed = JSON.parse(localStorage.getItem('menuCollapseState') ?? '{}');

        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            return {};
        }

        return Object.fromEntries(Object.entries(parsed).filter(([, v]) => typeof v === 'boolean')) as Record<
            string,
            boolean
        >;
    } catch {
        return {};
    }
}

function findMenu(m: MenuItem[], badge: string): MenuItem | undefined {
    for (const item of m) {
        if (item.name === badge) {
            return item;
        }
        if (item.sub) {
            const found = findMenu(item.sub, badge);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

function findParentMenu(m: MenuItem[], targetName: string): MenuItem | undefined {
    for (const item of m) {
        if (item.sub?.some((subItem: MenuItem) => subItem.name === targetName)) {
            return item;
        }
        if (item.sub) {
            const found = findParentMenu(item.sub, targetName);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

export const useMenuStore = defineStore('menu', {
    state: (): MenuState => {
        return {
            menu: menu.main ?? [],
            userRoles: [],
            isSuperAdmin: false,
            sectionCollapseState: loadCollapseState(),
        };
    },
    getters: {
        visibleMenu(state): MenuItem[] {
            // Role-based menu gating is deferred until backend role taxonomy
            // (owner/admin/user) is aligned with menu role labels.
            // All authenticated users see the full menu for now.
            return state.menu;
        },

        isSectionCollapsed(state): (name: string) => boolean {
            return (name: string) => state.sectionCollapseState[name] ?? false;
        },
    },
    actions: {
        setUserRoles(roles: string[], isSuperAdmin: boolean) {
            this.userRoles = roles;
            this.isSuperAdmin = isSuperAdmin;
        },

        toggleSection(name: string) {
            this.sectionCollapseState[name] = !(this.sectionCollapseState[name] ?? false);
            localStorage.setItem('menuCollapseState', JSON.stringify(this.sectionCollapseState));
        },

        addBadge(badge: string, count: number, variant: BadgeVariant = 'primary') {
            const m = findMenu(this.menu, badge);

            if (!m) {
                return;
            }

            m.badge = count.toString();
            m['badge-variant'] = variant;

            // Find and open parent menu if this is a sub-item
            const parent = findParentMenu(this.menu, badge);
            if (parent) {
                parent.open = true;
            }
        },

        removeBadge(badge: string) {
            const m = findMenu(this.menu, badge);

            if (!m) {
                return;
            }

            delete m.badge;
            delete m['badge-variant'];
        },
    },
});
